import { useMemo, useState } from 'react';
import { PiiClass, RedactionMode, type PolicySpec } from '@reef/schema';
import { applyPolicyOperations, type PolicyOperation } from '@reef/policy';
import { api } from '../api.ts';
import { AlreadyExistsError } from '../http.ts';
import { useSidebarPane } from '../components/PaneResizer.tsx';

/**
 * The policy editor.
 *
 * Every control on this screen emits a `PolicyOperation`, and so does the chat
 * box — that shared vocabulary is what stops "only allow 10 rows from users"
 * from being able to quietly drop an unrelated deny pattern: neither editor can
 * hand back a whole new document, only a set of named edits.
 *
 * The screen this replaced showed the rules and could change five of them, all
 * through fixed buttons — "Cap at 10 rows" and nothing that could cap at 50 —
 * so anybody who wanted a different number went to the config repo, and the
 * screen that called itself the policy editor was a reader with four shortcuts.
 * Now every field in `PolicySpec` is editable here, including the removals:
 * `allow_tables` with no `remove_tables` beside it made the editor a ratchet
 * that could only ever tighten.
 *
 * Two things are deliberate about how it reads.
 *
 * **Colour carries meaning or it is not used.** The stylesheet's first rule, and
 * this screen was the worst offender against it: a row ceiling wore the accent,
 * `read-only` wore the amber that means "act on this" — for the safest setting
 * on the page — and an allowlisted table wore the green that means "this is
 * fine", which made granting access look like a reassurance. So ceilings are
 * labelled values rather than chips, names are neutral chips, amber means the
 * agent's data was altered, red means the call was refused, and the accent is
 * only ever a toggle that is on.
 *
 * **What is on screen is the policy as it will be, with the pending edits
 * applied.** The queue is applied locally with the same
 * `applyPolicyOperations` the Worker runs, so a refusal — emptying an allowlist
 * blocks every query rather than widening it — is reported while the reader is
 * still looking at the control, not after they press Commit.
 */
export function PolicyView({
  policies,
  isAdmin,
  onChanged,
}: {
  policies: PolicySpec[];
  isAdmin: boolean;
  onChanged(): void;
}) {
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [deletedId, setDeletedId] = useState<string | null>(null);
  const [pending, setPending] = useState<PolicyOperation[]>([]);
  const [changes, setChanges] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  const { listed, committed } = selectPolicy(policies, { chosenId, deletedId });
  const selectedId = committed?.id ?? null;
  const pane = useSidebarPane('reef.policies.sidebar', 232);

  /*
   * The policy as the queue would leave it.
   *
   * `useMemo` over the same function the Worker applies, so the preview cannot
   * disagree with the commit. A queue that no longer applies — somebody else
   * committed underneath it — surfaces here as the refusal rather than as a
   * screen drawing rules that are not going to be saved.
   */
  const { preview, queued, cancelled, refusal } = useMemo(() => {
    const idle = { preview: committed, queued: [], cancelled: false, refusal: null };
    if (!committed) return { ...idle, preview: null };
    if (pending.length === 0) return idle;
    try {
      const { policy, changes } = applyPolicyOperations(committed, pending);
      return {
        preview: policy,
        queued: changes,
        /*
         * Every operation reports itself, so a queue can be a list of changes
         * and still add up to the document it started from: set the row ceiling
         * to 25, think better of it, set it back to 100. Both operations are
         * real edits and the changelog names both, but the policy they produce
         * is the committed one — and committing that writes a revision whose
         * only diff is the version integer, which is the thing this editor
         * refuses to do everywhere else.
         */
        cancelled: sameRules(policy, committed),
        refusal: null,
      };
    } catch (caught) {
      return { ...idle, queued: [], refusal: (caught as Error).message };
    }
  }, [committed, pending]);

  const policy = preview;
  const editable = isAdmin && !busy;

  const reset = () => {
    setPending([]);
    setChanges([]);
    setError(null);
  };

  /**
   * Queue an operation — or refuse it, or drop it as a no-op.
   *
   * All three decisions come from applying the queue with the same
   * `applyPolicyOperations` the Worker runs, which is what lets this happen at
   * the control rather than at the commit:
   *
   * - **Refused.** Removing the last table from an allowlist has two opposite
   *   readings, so it is not queued at all: the chip stays where it is and the
   *   banner names the operation that means the wide one. Queueing it and
   *   reporting the problem later would leave the reader looking at a screen
   *   they cannot save and a chip they cannot get back.
   * - **A no-op.** A number field that lost focus without being typed into, a
   *   chip removed and added back. A queue holding those offers a Commit
   *   button for a commit whose only diff is the version integer.
   * - **A change.** Appended, and the whole screen re-renders as the policy
   *   that queue would produce.
   */
  const queue = (operation: PolicyOperation) => {
    if (!committed) return;

    const next = [...pending, operation];
    let applied;
    try {
      applied = applyPolicyOperations(committed, next);
    } catch (caught) {
      setError((caught as Error).message);
      return;
    }

    setError(null);
    if (applied.changes.length === queued.length) return;
    setPending(next);
  };

  const apply = async () => {
    if (!committed || pending.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.editPolicy(committed.id, pending, 'edited in the policy editor');
      setChanges(result.changes);
      setPending([]);
      onChanged();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!committed) return;
    setBusy(true);
    setError(null);
    try {
      await api.deletePolicy(committed.id, 'deleted in the policy editor');
      setDeletedId(committed.id);
      setChosenId(null);
      reset();
      onChanged();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Interpret a plain-language instruction locally.
   *
   * This deliberately handles only the phrasings it can parse unambiguously and
   * says so otherwise, rather than guessing. The full natural-language path is
   * the Builder agent, which has the whole catalog in context; this is the
   * fast path for the edits people actually type into a policy screen.
   */
  const interpret = () => {
    const operation = parseInstruction(instruction);
    if (!operation) {
      setError(
        "I couldn't turn that into a policy change. Try something like “only allow 10 rows from users”, “make this read-only”, “redact email, ssn”, or “hide the columns ssn, phone”.",
      );
      return;
    }
    queue(operation);
    setInstruction('');
  };

  return (
    <div {...pane.body}>
      <aside className="sidebar">
        <p className="section-title">Policies</p>
        {listed.map((entry) => (
          <button
            key={entry.id}
            className="list-item"
            aria-current={entry.id === selectedId}
            onClick={() => {
              setChosenId(entry.id);
              reset();
            }}
          >
            {entry.id}
            <small>{summarize(entry)}</small>
          </button>
        ))}

        {isAdmin && (
          <button className="ghost" style={{ marginTop: '0.5rem' }} onClick={() => setCreating(true)}>
            + New policy
          </button>
        )}
      </aside>

      {pane.resizer}

      <main className="main">
        {creating && (
          <NewPolicyCard
            taken={listed.map((entry) => entry.id)}
            onCancel={() => setCreating(false)}
            onCreated={(id) => {
              setCreating(false);
              setChosenId(id);
              reset();
              onChanged();
            }}
          />
        )}

        {!policy && !creating && (
          <div className="empty">
            {listed.length === 0
              ? 'No policies yet. A step with no policy runs with reef’s defaults — 100 rows, 128 KB, nothing redacted.'
              : 'Pick a policy to read it.'}
          </div>
        )}

        {policy && committed && (
          <>
            <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>{policy.id}</h1>
            <p className="hint" style={{ marginTop: 0 }}>
              version {committed.version} · <code>policies/{policy.id}.json</code>
              {!isAdmin && ' · read-only: changing a policy needs workspace admin'}
            </p>

            <div className="card">
              <h2>What this policy does</h2>
              <p style={{ margin: 0, fontSize: '0.8125rem', maxWidth: '78ch' }}>
                {sentenceFor(policy)}
              </p>
              <p className="hint" style={{ marginBottom: 0 }}>
                Every rule below is enforced on the way back from a tool call, before the model
                sees a byte of the result.
              </p>
            </div>

            {error && (
              <div className="banner danger">
                <h3>Not applied</h3>
                <p style={{ margin: 0 }}>{error}</p>
              </div>
            )}

            {refusal && (
              <div className="banner danger">
                <h3>That edit is refused</h3>
                <p style={{ margin: 0 }}>{refusal}</p>
              </div>
            )}

            {changes.length > 0 && (
              <div className="banner">
                <h3>Committed</h3>
                <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                  {changes.map((change) => (
                    <li key={change}>{change}</li>
                  ))}
                </ul>
              </div>
            )}

            <LimitsCard policy={policy} editable={editable} onQueue={queue} />
            <TablesCard policy={policy} editable={editable} onQueue={queue} />
            <ColumnsCard policy={policy} editable={editable} onQueue={queue} />
            <RedactionCard policy={policy} editable={editable} onQueue={queue} />
            <PerTableCard policy={policy} editable={editable} onQueue={queue} />
            <DenyPatternsCard policy={policy} editable={editable} onQueue={queue} />

            {isAdmin && (
              <div className="card">
                <h2>Change it in words</h2>
                <div className="stack">
                  <input
                    value={instruction}
                    placeholder="only allow 10 rows from users"
                    disabled={!editable}
                    onChange={(event) => setInstruction(event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && interpret()}
                  />
                  <div className="row">
                    <button onClick={interpret} disabled={!editable || !instruction.trim()}>
                      Queue this change
                    </button>
                    <span className="hint">
                      Turns into the same operations the controls above emit — nothing a chat box
                      can do here is beyond what the visual editor can.
                    </span>
                  </div>
                </div>
              </div>
            )}

            {pending.length > 0 && (
              <div className="card">
                <h2>Not saved yet ({pending.length})</h2>
                <ul style={{ margin: '0 0 0.75rem', paddingLeft: '1.1rem', fontSize: '0.8125rem' }}>
                  {queued.map((change, index) => (
                    <li key={`${change}-${index}`}>{change}</li>
                  ))}
                </ul>
                {/*
                 * The operations themselves, behind a disclosure. They are the
                 * contract between the two editors rather than decoration —
                 * the claim above the chat box is that a typed sentence and a
                 * control produce the same operation, and this is where you
                 * check it.
                 */}
                <details>
                  <summary className="hint">The operations this will send</summary>
                  <pre>{JSON.stringify(pending, null, 2)}</pre>
                </details>
                <div className="row" style={{ marginTop: '0.75rem' }}>
                  <button
                    className="primary"
                    onClick={() => void apply()}
                    disabled={busy || cancelled || refusal !== null}
                  >
                    Commit to the repo
                  </button>
                  <button className="ghost" onClick={reset}>
                    Discard
                  </button>
                  <span className="hint">
                    {cancelled
                      ? 'These edits cancel out — the policy is already what they would make it.'
                      : (
                          <>
                            A policy is a commit — <code>policies/{policy.id}.json</code>,
                            attributed to you.
                          </>
                        )}
                  </span>
                </div>
              </div>
            )}

            {isAdmin && (
              <div className="card">
                <h2>Delete this policy</h2>
                <div className="row">
                  <button className="ghost danger" disabled={busy} onClick={() => void remove()}>
                    Delete {policy.id}
                  </button>
                  <span className="hint">
                    Workflows naming it are not rewritten — they are refused on their next run
                    rather than running unguarded.
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

/**
 * Whether two policies say the same thing.
 *
 * Every field but `version`, which moves on its own and is the one difference
 * that is not a difference. Both sides have been through `parsePolicy` — the
 * preview by way of `applyPolicyOperations` — so the key order is the schema's
 * and the serialisations are comparable.
 */
function sameRules(a: PolicySpec, b: PolicySpec): boolean {
  return JSON.stringify({ ...a, version: 0 }) === JSON.stringify({ ...b, version: 0 });
}

// -------------------------------------------------------------- the selection

export interface PolicySelection {
  /** The policy the reader last clicked, or null if they have not clicked one. */
  chosenId: string | null;
  /** A policy this session deleted, which the shell's list may not have dropped yet. */
  deletedId: string | null;
}

/**
 * Which policy the screen is showing, and which ones the rail lists.
 *
 * Derived during render rather than seeded into state, and exported rather than
 * inlined, because the three transitions it has to get right are the ones a
 * browser cannot reach here: creating and deleting a policy are commits, so
 * neither succeeds on a deployment without a GitHub App, and the e2e suite does
 * not have one. `apps/web/CLAUDE.md` names exactly this case as the one that
 * earns a unit test.
 *
 * Each branch is a bug that shipped or was caught in review:
 *
 * - **Nothing chosen** selects the first policy. `useState(policies[0]?.id)`
 *   looked equivalent and was not: the shell fetches the list, so the first
 *   render has none, the initial value was captured as null and never
 *   revisited, and the screen said "pick a policy to read it" beside three of
 *   them until somebody clicked.
 * - **Chosen but absent** selects nothing. A create sets `chosenId` to an id the
 *   list does not hold until the refresh lands, and falling back to the first
 *   policy there drew *its* rules — with working Commit and Delete — under a
 *   heading naming the policy that had just been created.
 * - **Deleted and still listed** also selects nothing, and drops the row from
 *   the rail. Clearing the selection alone was not enough: it made "nothing
 *   chosen" true again, so the fallback moved to whichever policy sorts first
 *   and put a live Delete button back under the cursor that had just pressed
 *   one. Two clicks, two policies. The row goes too, so the rail cannot offer a
 *   policy whose document is already gone.
 */
export function selectPolicy(
  policies: readonly PolicySpec[],
  { chosenId, deletedId }: PolicySelection,
): { listed: PolicySpec[]; committed: PolicySpec | null } {
  const listed = policies.filter((entry) => entry.id !== deletedId);
  // The refresh has not landed while the list still carries what was deleted.
  const awaitingRefresh = deletedId !== null && listed.length !== policies.length;

  const chosen = chosenId === null ? null : listed.find((entry) => entry.id === chosenId) ?? null;
  if (chosen) return { listed, committed: chosen };
  if (chosenId !== null || awaitingRefresh) return { listed, committed: null };
  return { listed, committed: listed[0] ?? null };
}

// ------------------------------------------------------------------ the cards

/**
 * The ceilings, and whether writes are refused.
 *
 * Labelled values with number fields, not chips. A ceiling is a quantity a
 * reader compares against the one they had in mind — "is 100 enough for this
 * report" — and a capsule is the wrong shape for a number you might change.
 */
function LimitsCard({
  policy,
  editable,
  onQueue,
}: {
  policy: PolicySpec;
  editable: boolean;
  onQueue(operation: PolicyOperation): void;
}) {
  return (
    <div className="card">
      <h2>Limits</h2>
      <div className="row wrap" style={{ gap: '1.25rem', alignItems: 'flex-start' }}>
        <NumberField
          id="policy-max-rows"
          label="Rows per call"
          value={policy.maxRows}
          min={0}
          editable={editable}
          hint="Extra rows are dropped and the model is told the result is partial."
          onCommit={(maxRows) => onQueue({ op: 'set_max_rows', maxRows })}
        />
        <NumberField
          id="policy-max-kb"
          label="Result size (KB)"
          value={Math.round(policy.maxBytes / 1024)}
          min={1}
          editable={editable}
          hint="Applied after the row limit, over the serialized result."
          onCommit={(kb) => onQueue({ op: 'set_max_bytes', maxBytes: kb * 1024 })}
        />
        <div className="field">
          <label>Writes</label>
          <label className="row" style={{ gap: '0.4rem' }}>
            <input
              type="checkbox"
              checked={policy.readOnly}
              disabled={!editable}
              onChange={(event) => onQueue({ op: 'set_read_only', readOnly: event.target.checked })}
            />
            <span>Refuse anything that would change data</span>
          </label>
          <span className="hint">
            {policy.readOnly
              ? 'An insert, update, delete or DDL statement is blocked before it runs.'
              : 'Writes are allowed — a step using this policy can change data.'}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Which tables a query may touch at all. */
function TablesCard({
  policy,
  editable,
  onQueue,
}: {
  policy: PolicySpec;
  editable: boolean;
  onQueue(operation: PolicyOperation): void;
}) {
  return (
    <div className="card">
      <h2>Tables</h2>
      {policy.tableAllowlist ? (
        <>
          <ChipEditor
            id="policy-tables"
            label="Only these tables may be queried"
            values={policy.tableAllowlist}
            placeholder="add a table"
            editable={editable}
            onAdd={(table) => onQueue({ op: 'allow_tables', tables: [table] })}
            onRemove={(table) => onQueue({ op: 'remove_tables', tables: [table] })}
          />
          {editable && (
            <button
              className="ghost"
              style={{ marginTop: '0.5rem' }}
              onClick={() => onQueue({ op: 'clear_table_allowlist' })}
            >
              Allow every table
            </button>
          )}
        </>
      ) : (
        <div className="stack">
          <p className="hint" style={{ margin: 0 }}>
            Every table is queryable. A query naming a table outside an allowlist is blocked
            before it runs, so this is the cheapest rule on the page to add.
          </p>
          {editable && (
            <ChipEditor
              id="policy-tables"
              label="Restrict to named tables"
              values={[]}
              placeholder="first table to allow"
              editable={editable}
              onAdd={(table) => onQueue({ op: 'allow_tables', tables: [table] })}
              onRemove={() => undefined}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The two column lists that apply to every table.
 *
 * Side by side because the pair is one decision read two ways, and because
 * their asymmetry matters: an empty deny list denies nothing, while an empty
 * allow list would strip every column from every row — so the allow side has
 * a clear button and its last chip cannot simply be removed.
 */
function ColumnsCard({
  policy,
  editable,
  onQueue,
}: {
  policy: PolicySpec;
  editable: boolean;
  onQueue(operation: PolicyOperation): void;
}) {
  return (
    <div className="card">
      <h2>Columns</h2>
      <div className="row wrap" style={{ gap: '1.25rem', alignItems: 'flex-start' }}>
        <div className="field" style={{ flex: 1, minWidth: '16rem' }}>
          <ChipEditor
            id="policy-allowed-columns"
            label={
              policy.allowedColumns ? 'Only these columns are returned' : 'Return only named columns'
            }
            values={policy.allowedColumns ?? []}
            placeholder="add a column"
            editable={editable}
            onAdd={(column) => onQueue({ op: 'allow_columns', columns: [column] })}
            onRemove={(column) => onQueue({ op: 'remove_allowed_columns', columns: [column] })}
          />
          {policy.allowedColumns && editable && (
            <button
              className="ghost"
              style={{ marginTop: '0.35rem' }}
              onClick={() => onQueue({ op: 'clear_allowed_columns' })}
            >
              Return every column
            </button>
          )}
        </div>

        <div className="field" style={{ flex: 1, minWidth: '16rem' }}>
          <ChipEditor
            id="policy-denied-columns"
            label="Never returned"
            values={policy.deniedColumns ?? []}
            placeholder="e.g. password_hash"
            editable={editable}
            onAdd={(column) => onQueue({ op: 'deny_columns', columns: [column] })}
            onRemove={(column) => onQueue({ op: 'remove_denied_columns', columns: [column] })}
          />
          <span className="hint">Applied after the allowlist, so this always wins.</span>
        </div>
      </div>
    </div>
  );
}

/**
 * What is detected in values and rewritten.
 *
 * The classes are toggles, which is the one place the accent belongs on this
 * screen: `button.pill[aria-pressed]` fills and carries a tick when it is on,
 * so "which of these eight am I redacting" is answerable from across the room.
 */
function RedactionCard({
  policy,
  editable,
  onQueue,
}: {
  policy: PolicySpec;
  editable: boolean;
  onQueue(operation: PolicyOperation): void;
}) {
  return (
    <div className="card">
      <h2>Redaction</h2>
      <div className="stack">
        <div className="field">
          <label>Detect and rewrite</label>
          <div className="row wrap">
            {PiiClass.options.map((klass) => {
              const on = policy.redactPii.includes(klass);
              return (
                <button
                  key={klass}
                  className="pill"
                  /*
                   * Labelled explicitly, because a pressed pill draws its tick
                   * with a `::before` and Chromium folds generated content
                   * into the accessible name — so the eight toggles announced
                   * themselves as "✓ssn" when on and "ssn" when off, which is
                   * the state already carried by `aria-pressed`.
                   */
                  aria-label={`Redact ${klass}`}
                  aria-pressed={on}
                  disabled={!editable}
                  onClick={() =>
                    onQueue(
                      on
                        ? { op: 'remove_pii', classes: [klass] }
                        : { op: 'redact_pii', classes: [klass] },
                    )
                  }
                >
                  {klass}
                </button>
              );
            })}
          </div>
          <span className="hint">
            A safety net over free text, not the primary control — a column allowlist is. The
            detectors will not catch every phrasing.
          </span>
        </div>

        <div className="field" style={{ maxWidth: '22rem' }}>
          <label htmlFor="policy-redaction-mode">How a match is rewritten</label>
          <select
            id="policy-redaction-mode"
            value={policy.redactionMode}
            disabled={!editable}
            onChange={(event) =>
              onQueue({
                op: 'set_redaction_mode',
                mode: event.target.value as PolicySpec['redactionMode'],
              })
            }
          >
            {RedactionMode.options.map((mode) => (
              <option key={mode} value={mode}>
                {mode} — {REDACTION_MODES[mode]}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

/** Per-table overrides, which is where a real policy does most of its work. */
function PerTableCard({
  policy,
  editable,
  onQueue,
}: {
  policy: PolicySpec;
  editable: boolean;
  onQueue(operation: PolicyOperation): void;
}) {
  const [table, setTable] = useState('');

  return (
    <div className="card">
      <h2>Per-table rules</h2>
      <p className="hint" style={{ marginTop: 0 }}>
        A table with no rule here falls back to the limits above. A rule tightens one table
        without touching the others.
      </p>

      <div className="stack">
        {policy.tables.map((rule) => (
          <div key={rule.table} className="trigger-row">
            <div className="row">
              <strong style={{ flex: 1 }}>
                <code>{rule.table}</code>
              </strong>
              {editable && (
                <button
                  className="ghost"
                  onClick={() => onQueue({ op: 'remove_table_rule', table: rule.table })}
                >
                  Remove rule
                </button>
              )}
            </div>

            <div className="row wrap" style={{ gap: '1.25rem', alignItems: 'flex-start' }}>
              <NumberField
                id={`policy-rows-${rule.table}`}
                label="Rows per call"
                value={rule.maxRows ?? policy.maxRows}
                min={0}
                editable={editable}
                hint={rule.maxRows === undefined ? 'Inherited from the policy limit.' : undefined}
                onCommit={(maxRows) =>
                  onQueue({ op: 'set_table_max_rows', table: rule.table, maxRows })
                }
              />
              <div className="field" style={{ flex: 1, minWidth: '14rem' }}>
                <ChipEditor
                  id={`policy-allow-${rule.table}`}
                  label="Only these columns"
                  values={rule.allowedColumns ?? []}
                  placeholder="add a column"
                  editable={editable}
                  onAdd={(column) =>
                    onQueue({ op: 'allow_columns', columns: [column], table: rule.table })
                  }
                  onRemove={(column) =>
                    onQueue({
                      op: 'remove_allowed_columns',
                      columns: [column],
                      table: rule.table,
                    })
                  }
                />
                {rule.allowedColumns && editable && (
                  <button
                    className="ghost"
                    style={{ marginTop: '0.35rem' }}
                    onClick={() => onQueue({ op: 'clear_allowed_columns', table: rule.table })}
                  >
                    Return every column
                  </button>
                )}
              </div>
              <div className="field" style={{ flex: 1, minWidth: '14rem' }}>
                <ChipEditor
                  id={`policy-deny-${rule.table}`}
                  label="Never returned"
                  values={rule.deniedColumns ?? []}
                  placeholder="add a column"
                  editable={editable}
                  onAdd={(column) =>
                    onQueue({ op: 'deny_columns', columns: [column], table: rule.table })
                  }
                  onRemove={(column) =>
                    onQueue({ op: 'remove_denied_columns', columns: [column], table: rule.table })
                  }
                />
              </div>
            </div>
          </div>
        ))}

        {policy.tables.length === 0 && <p className="hint">No per-table rules.</p>}

        {editable && (
          <div className="row">
            <input
              aria-label="Table to add a rule for"
              placeholder="table name"
              value={table}
              onChange={(event) => setTable(event.target.value)}
            />
            <button
              disabled={!table.trim()}
              onClick={() => {
                // A rule has to start with a value, and the row ceiling is the
                // one every table has — so a new rule pins the policy's own
                // limit rather than inventing a tighter one nobody chose.
                onQueue({
                  op: 'set_table_max_rows',
                  table: table.trim(),
                  maxRows: policy.maxRows,
                });
                setTable('');
              }}
            >
              Add a rule
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Patterns matched over the serialized call, and what happens when one hits.
 *
 * The only place on the screen where a tone is a severity, because this is the
 * only place with two of them: `block` fails the call and wears the danger
 * tone, `redact` lets it through altered and wears the amber. Those are
 * different promises and the difference has to be visible without reading.
 */
function DenyPatternsCard({
  policy,
  editable,
  onQueue,
}: {
  policy: PolicySpec;
  editable: boolean;
  onQueue(operation: PolicyOperation): void;
}) {
  const [draft, setDraft] = useState({
    id: '',
    pattern: '',
    action: 'block' as 'block' | 'redact',
    appliesTo: 'both' as 'input' | 'output' | 'both',
    replacement: '[redacted]',
    reason: '',
  });

  const add = () => {
    const id = draft.id.trim();
    const pattern = draft.pattern.trim();
    if (!id || !pattern) return;
    try {
      new RegExp(pattern);
    } catch {
      // Refused here rather than at the commit: a pattern that does not compile
      // reads as protection on the screen and matches nothing at runtime.
      return;
    }
    onQueue({
      op: 'add_deny_pattern',
      id,
      pattern,
      action: draft.action,
      appliesTo: draft.appliesTo,
      replacement: draft.replacement || '[redacted]',
      ...(draft.reason.trim() ? { reason: draft.reason.trim() } : {}),
    });
    setDraft({
      id: '',
      pattern: '',
      action: 'block',
      appliesTo: 'both',
      replacement: '[redacted]',
      reason: '',
    });
  };

  const patternValid = (() => {
    if (!draft.pattern.trim()) return true;
    try {
      new RegExp(draft.pattern);
      return true;
    } catch {
      return false;
    }
  })();

  return (
    <div className="card">
      <h2>Deny patterns</h2>
      <p className="hint" style={{ marginTop: 0 }}>
        Matched over the serialized call. <strong>Block</strong> fails the call outright —
        the model gets an error rather than partial data. <strong>Redact</strong> lets it through
        with every match replaced.
      </p>

      {policy.denyPatterns.length > 0 ? (
        <table>
          <thead>
            <tr>
              <th>Id</th>
              <th>Pattern</th>
              <th>Scans</th>
              <th>When it matches</th>
              {editable && <th />}
            </tr>
          </thead>
          <tbody>
            {policy.denyPatterns.map((pattern) => (
              <tr key={pattern.id}>
                <td>{pattern.id}</td>
                <td>
                  <code>{pattern.pattern}</code>
                </td>
                <td>{pattern.appliesTo}</td>
                <td>
                  <span className={`pill ${pattern.action === 'block' ? 'danger' : 'warn'}`}>
                    {pattern.action}
                  </span>
                </td>
                {editable && (
                  <td>
                    <button
                      className="ghost"
                      onClick={() => onQueue({ op: 'remove_deny_pattern', id: pattern.id })}
                    >
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="hint">No deny patterns.</p>
      )}

      {editable && (
        <div className="stack" style={{ marginTop: '0.75rem' }}>
          <div className="row wrap" style={{ alignItems: 'flex-end' }}>
            <div className="field" style={{ minWidth: '10rem' }}>
              <label htmlFor="deny-id">Id</label>
              <input
                id="deny-id"
                placeholder="no-select-star"
                value={draft.id}
                onChange={(event) => setDraft({ ...draft, id: event.target.value })}
              />
            </div>
            <div className="field" style={{ flex: 1, minWidth: '14rem' }}>
              <label htmlFor="deny-pattern">Pattern (regular expression)</label>
              <input
                id="deny-pattern"
                placeholder="select\s+\*"
                value={draft.pattern}
                aria-invalid={!patternValid}
                onChange={(event) => setDraft({ ...draft, pattern: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="deny-action">When it matches</label>
              <select
                id="deny-action"
                value={draft.action}
                onChange={(event) =>
                  setDraft({ ...draft, action: event.target.value as 'block' | 'redact' })
                }
              >
                <option value="block">block the call</option>
                <option value="redact">redact the match</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="deny-applies">Scans</label>
              <select
                id="deny-applies"
                value={draft.appliesTo}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    appliesTo: event.target.value as 'input' | 'output' | 'both',
                  })
                }
              >
                <option value="both">both</option>
                <option value="input">input</option>
                <option value="output">output</option>
              </select>
            </div>
          </div>
          <div className="row">
            {/* Only for `redact`: a replacement on a `block` pattern is a
              * field that reads as configuration and changes nothing, because
              * a blocked call has no result left to substitute into. */}
            {draft.action === 'redact' && (
              <div className="field" style={{ minWidth: '10rem' }}>
                <label htmlFor="deny-replacement">Replace matches with</label>
                <input
                  id="deny-replacement"
                  value={draft.replacement}
                  onChange={(event) => setDraft({ ...draft, replacement: event.target.value })}
                />
              </div>
            )}
            <input
              aria-label="Why this pattern exists"
              placeholder="why — shown to the model when the call is blocked"
              value={draft.reason}
              onChange={(event) => setDraft({ ...draft, reason: event.target.value })}
            />
            <button disabled={!draft.id.trim() || !draft.pattern.trim() || !patternValid} onClick={add}>
              Add pattern
            </button>
          </div>
          {!patternValid && (
            <span className="hint">
              That is not a valid regular expression, so it would match nothing at runtime while
              reading as protection here.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Creating a policy.
 *
 * Deliberately not "an empty document to fill in": a new policy starts at
 * reef's defaults — 100 rows, 128 KB, writes allowed, nothing redacted — which
 * is what a step with no policy already gets, so the first edit a reader makes
 * is the first thing that policy actually adds. The two fields here are the
 * only ones that cannot be edited afterwards or guessed.
 */
function NewPolicyCard({
  taken,
  onCancel,
  onCreated,
}: {
  taken: readonly string[];
  onCancel(): void;
  onCreated(id: string): void;
}) {
  const [id, setId] = useState('');
  const [description, setDescription] = useState('');
  const [readOnly, setReadOnly] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const clash = taken.includes(id.trim());
  const shape = /^[a-z0-9][a-z0-9-]*$/.test(id.trim());

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.createPolicy(
        id.trim(),
        { description: description.trim() || undefined, readOnly },
        'created in the policy editor',
      );
      onCreated(id.trim());
    } catch (caught) {
      setError(
        caught instanceof AlreadyExistsError
          ? `a policy named "${id.trim()}" already exists — pick another id`
          : (caught as Error).message,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h2>New policy</h2>
      <div className="stack">
        <div className="field">
          <label htmlFor="new-policy-id">Id</label>
          <input
            id="new-policy-id"
            placeholder="prod-db-restricted"
            value={id}
            onChange={(event) => setId(event.target.value)}
          />
          <span className="hint">
            Lower case, dashes. This is the name a step carries and the file it commits to —
            <code> policies/{id.trim() || '<id>'}.json</code>.
          </span>
        </div>

        <div className="field">
          <label htmlFor="new-policy-description">What it is for</label>
          <input
            id="new-policy-description"
            placeholder="Production database access for agents. Read-only."
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>

        <label className="row" style={{ gap: '0.4rem' }}>
          <input
            type="checkbox"
            checked={readOnly}
            onChange={(event) => setReadOnly(event.target.checked)}
          />
          <span>Read-only to start with</span>
        </label>

        {clash && <span className="hint">That id is taken.</span>}
        {error && (
          <div className="banner danger">
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        )}

        <div className="row">
          <button
            className="primary"
            disabled={busy || !shape || clash}
            onClick={() => void create()}
          >
            Create and commit
          </button>
          <button className="ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- the pieces

/**
 * A number a reader might change, held locally until they are done with it.
 *
 * The draft is the whole point: a field that queued an operation per keystroke
 * turned "100" into "1", "10" and "100" — three edits, two of them ceilings
 * nobody chose. It commits on blur and on Enter, and only when the value moved.
 */
function NumberField({
  id,
  label,
  value,
  min,
  hint,
  editable,
  onCommit,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  hint?: string | undefined;
  editable: boolean;
  onCommit(value: number): void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  const commit = () => {
    if (draft === null) return;
    /*
     * A cleared field is an unfinished edit, not a zero. `Number('')` is 0 and
     * the row ceiling's floor *is* 0 — a policy that returns nothing is a
     * legitimate thing to write — so emptying the box and clicking away used to
     * queue that ceiling rather than abandoning the edit. The byte field was
     * accidentally safe because its floor is 1.
     */
    if (!draft.trim()) {
      setDraft(null);
      return;
    }
    const parsed = Number(draft);
    setDraft(null);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < min) return;
    if (parsed === value) return;
    onCommit(parsed);
  };

  return (
    <div className="field" style={{ minWidth: '9rem' }}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        min={min}
        value={draft ?? String(value)}
        disabled={!editable}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit();
          if (event.key === 'Escape') setDraft(null);
        }}
      />
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

/**
 * A list of names, as chips with an × and a box to add one.
 *
 * Neutral, every one of them: a table name and a column name are names, not
 * statuses, and the green they used to wear said "this is fine" about a grant
 * of access.
 */
function ChipEditor({
  id,
  label,
  values,
  placeholder,
  editable,
  onAdd,
  onRemove,
}: {
  id: string;
  label: string;
  values: readonly string[];
  placeholder: string;
  editable: boolean;
  onAdd(value: string): void;
  onRemove(value: string): void;
}) {
  const [draft, setDraft] = useState('');

  const add = () => {
    const entry = draft.trim();
    if (!entry) return;
    onAdd(entry);
    setDraft('');
  };

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>

      {values.length > 0 && (
        <div className="row wrap" style={{ gap: '0.25rem' }}>
          {values.map((value) => (
            <span className="pill" key={value}>
              {value}
              {editable && (
                <button className="chip-x" aria-label={`Remove ${value}`} onClick={() => onRemove(value)}>
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {editable && (
        <input
          id={id}
          value={draft}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ',') {
              event.preventDefault();
              add();
            }
          }}
          onBlur={add}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------- wording

const REDACTION_MODES: Record<PolicySpec['redactionMode'], string> = {
  mask: 'keep the shape, hide the value (ab***@***.com)',
  hash: 'a stable token, so rows can still be correlated',
  drop: 'remove the field entirely',
  null: 'keep the field, null the value',
};

/** The mode as something a sentence can end on — `drop` is not "dropped". */
const REDACTION_VERBS: Record<PolicySpec['redactionMode'], string> = {
  mask: 'masked',
  hash: 'replaced with a stable token',
  drop: 'removed',
  null: 'nulled',
};

/** The rail's second line: the two facts that decide whether to attach this one. */
function summarize(policy: PolicySpec): string {
  const parts = [`${policy.maxRows} rows`];
  if (policy.readOnly) parts.push('read-only');
  if (policy.tableAllowlist) parts.push(`${policy.tableAllowlist.length} tables`);
  if (policy.redactPii.length > 0) parts.push(`${policy.redactPii.length} redacted`);
  return parts.join(' · ');
}

/**
 * The policy as a sentence.
 *
 * Every rule on the screen is also stated here in prose, because the question
 * somebody arrives with is "what does this let an agent do" and the answer used
 * to be assembled by reading six cards and knowing what an empty one meant.
 */
function sentenceFor(policy: PolicySpec): string {
  const clauses: string[] = [
    `reads at most ${policy.maxRows} rows and ${Math.round(policy.maxBytes / 1024)} KB per tool call`,
  ];

  clauses.push(policy.readOnly ? 'cannot change data' : 'may change data');

  if (policy.tableAllowlist) {
    clauses.push(`may only query ${policy.tableAllowlist.join(', ')}`);
  }
  if (policy.allowedColumns) {
    clauses.push(`sees only the columns ${policy.allowedColumns.join(', ')}`);
  }
  if (policy.deniedColumns?.length) {
    clauses.push(`never sees ${policy.deniedColumns.join(', ')}`);
  }
  if (policy.redactPii.length > 0) {
    clauses.push(`has ${policy.redactPii.join(', ')} ${REDACTION_VERBS[policy.redactionMode]}`);
  }

  const blocking = policy.denyPatterns.filter((pattern) => pattern.action === 'block').length;
  if (blocking > 0) {
    clauses.push(`is refused outright on ${blocking} pattern${blocking === 1 ? '' : 's'}`);
  }

  const last = clauses.pop();
  return `A step using ${policy.id} ${clauses.join(', ')}${clauses.length ? ' and ' : ''}${last}.`;
}

/**
 * The phrasings the local interpreter handles.
 *
 * Extracted so the parse is one expression per phrasing and the caller decides
 * what an unparsed sentence means — which is "say so", never "guess".
 */
function parseInstruction(instruction: string): PolicyOperation | null {
  const text = instruction.trim().toLowerCase();
  if (!text) return null;

  const tableRows = /(?:only\s+)?allow\s+(\d+)\s+rows?\s+from\s+(?:the\s+)?([a-z_][a-z0-9_]*)/.exec(text);
  if (tableRows) {
    return { op: 'set_table_max_rows', table: tableRows[2]!, maxRows: Number(tableRows[1]) };
  }

  const rows = /(?:max|limit|only)\s+(\d+)\s+rows?/.exec(text);
  if (rows) return { op: 'set_max_rows', maxRows: Number(rows[1]) };

  if (/read[- ]only|no writes|block writes/.test(text)) {
    return { op: 'set_read_only', readOnly: true };
  }

  const tables = /(?:only\s+)?allow\s+(?:the\s+)?tables?\s+([a-z_,\s]+)/.exec(text);
  if (tables) {
    const named = splitWords(tables[1]!);
    if (named.length > 0) return { op: 'allow_tables', tables: named };
  }

  const redact = /redact\s+([a-z_,\s]+)/.exec(text);
  if (redact) {
    const classes = splitWords(redact[1]!).filter((word): word is PolicySpec['redactPii'][number] =>
      (PiiClass.options as readonly string[]).includes(word),
    );
    if (classes.length > 0) return { op: 'redact_pii', classes };
  }

  const deny = /(?:hide|deny|drop)\s+(?:the\s+)?column[s]?\s+([a-z_,\s]+)/.exec(text);
  if (deny) {
    const columns = splitWords(deny[1]!);
    if (columns.length > 0) return { op: 'deny_columns', columns };
  }

  return null;
}

function splitWords(text: string): string[] {
  return text
    .split(/[,\s]+/)
    .map((word) => word.trim())
    .filter(Boolean);
}
